import { useSelect } from 'downshift';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useGlobalConfig } from '../../hooks/configContext';
import { cn } from '../../lib/classNames';
import { setLocaleCookie } from '../../lib/locale';
import { Otovo$Locale } from '../../types/otovoweb';
import { getBackgroundColorForDropdown } from '../Form/dropdownColorUtil';
import { Sanity$Document } from '../../types/sanityTypes';
import {
  useLocaleToPathMap,
  LocaleToPathReturnType,
} from './useLocaleToPathMap';
import { LanguageSwitcherPathResolver } from './types';

const localeToLanguageMap = {
  'de-ch': 'Deutsch',
  'fr-be': 'Français',
  'fr-ch': 'Français',
  'it-ch': 'Italiano',
  'nl-be': 'Nederlands',
};

type Props = {
  sanityDocument?: Sanity$Document;
  pathResolver?: LanguageSwitcherPathResolver;
};

export default function LanguageSwitcher({
  sanityDocument,
  pathResolver,
}: Props) {
  const router = useRouter();
  const { BU_CONFIG } = useGlobalConfig();

  const {
    localeToPathMap,
    isLoading: isLoadingUrlsForOtherLanguages,
    error: errorWhileLoadingUrlsForOtherLanguages,
  }: LocaleToPathReturnType = useLocaleToPathMap({
    marketRelevantLocales: BU_CONFIG.supported_locales,
    defaultLocale: BU_CONFIG.locale,
    currentPath: router.asPath,
    sanityDocument,
    pathResolver,
  });

  const [selectedLocale, setSelectedLocale] = useState(
    router.locale as Otovo$Locale,
  );

  function onLocaleChanged(locale: Otovo$Locale) {
    setSelectedLocale(locale);
    setLocaleCookie(locale);
    const path = localeToPathMap[locale];
    router.push(path, path, { locale });
  }

  const localeList = Object.keys(localeToPathMap || {}) as Otovo$Locale[];

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    highlightedIndex,
    getItemProps,
  } = useSelect<Otovo$Locale>({
    items: localeList,
    selectedItem: selectedLocale,
    onSelectedItemChange: ({ selectedItem }) => onLocaleChanged(selectedItem),
  });

  if (
    isLoadingUrlsForOtherLanguages ||
    errorWhileLoadingUrlsForOtherLanguages
  ) {
    return null;
  }

  return (
    <div className="relative">
      <button
        type="button"
        className="flex items-center justify-between rounded-sm border-none bg-grey-5 px-3 py-2 text-lg"
        {...getToggleButtonProps()}
        data-testid="language-switcher"
      >
        <div className="pr-2">
          <svg
            width="16px"
            height="16px"
            viewBox="0 0 16 16"
            xmlns="http://www.w3.org/2000/svg"
            fill="currentColor"
            className="bi bi-globe"
          >
            <path d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8zm7.5-6.923c-.67.204-1.335.82-1.887 1.855A7.97 7.97 0 0 0 5.145 4H7.5V1.077zM4.09 4a9.267 9.267 0 0 1 .64-1.539 6.7 6.7 0 0 1 .597-.933A7.025 7.025 0 0 0 2.255 4H4.09zm-.582 3.5c.03-.877.138-1.718.312-2.5H1.674a6.958 6.958 0 0 0-.656 2.5h2.49zM4.847 5a12.5 12.5 0 0 0-.338 2.5H7.5V5H4.847zM8.5 5v2.5h2.99a12.495 12.495 0 0 0-.337-2.5H8.5zM4.51 8.5a12.5 12.5 0 0 0 .337 2.5H7.5V8.5H4.51zm3.99 0V11h2.653c.187-.765.306-1.608.338-2.5H8.5zM5.145 12c.138.386.295.744.468 1.068.552 1.035 1.218 1.65 1.887 1.855V12H5.145zm.182 2.472a6.696 6.696 0 0 1-.597-.933A9.268 9.268 0 0 1 4.09 12H2.255a7.024 7.024 0 0 0 3.072 2.472zM3.82 11a13.652 13.652 0 0 1-.312-2.5h-2.49c.062.89.291 1.733.656 2.5H3.82zm6.853 3.472A7.024 7.024 0 0 0 13.745 12H11.91a9.27 9.27 0 0 1-.64 1.539 6.688 6.688 0 0 1-.597.933zM8.5 12v2.923c.67-.204 1.335-.82 1.887-1.855.173-.324.33-.682.468-1.068H8.5zm3.68-1h2.146c.365-.767.594-1.61.656-2.5h-2.49a13.65 13.65 0 0 1-.312 2.5zm2.802-3.5a6.959 6.959 0 0 0-.656-2.5H12.18c.174.782.282 1.623.312 2.5h2.49zM11.27 2.461c.247.464.462.98.64 1.539h1.835a7.024 7.024 0 0 0-3.072-2.472c.218.284.418.598.597.933zM10.855 4a7.966 7.966 0 0 0-.468-1.068C9.835 1.897 9.17 1.282 8.5 1.077V4h2.355z" />
          </svg>
        </div>
        <p className="text-sm">
          <span>{selectedLocale.split('-')[0].toLocaleUpperCase()}</span>
        </p>
      </button>
      <ul
        className={cn(
          'absolute z-50 mt-1 max-h-[200px] max-w-[200px] list-none rounded-sm border bg-grey-60 pl-0',
          isOpen ? 'border border-solid' : 'border-none',
        )}
        {...getMenuProps()}
        data-testid="language-switcher-dropdown"
      >
        {isOpen &&
          localeList.map((item, index) => (
            <li
              className={cn(
                'cursor-pointer p-2',
                getBackgroundColorForDropdown(
                  localeList,
                  selectedLocale,
                  highlightedIndex,
                  index,
                ),
              )}
              key={item}
              {...getItemProps({ item, index })}
              data-testid="language-switcher-dropdown-entry"
            >
              <p className="inline-block align-middle text-sm leading-3 text-blue-10">
                {localeToLanguageMap[item]}
              </p>
            </li>
          ))}
      </ul>
    </div>
  );
}
